import { ActivatedRouteSnapshot, RouteReuseStrategy, DetachedRouteHandle } from '@angular/router';
import { inject, Injectable } from '@angular/core';
import { AppRegistryService } from './services/app-registry.service';

@Injectable({ providedIn: 'root' })
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  private readonly registry = inject(AppRegistryService);
  private readonly handlers = new Map<string, DetachedRouteHandle>();

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    // Only detach leaf routes to prevent overlapping route handles which cause infinite loops
    if (!route.routeConfig || !route.routeConfig.component) {
      return false;
    }
    const url = this.getResolvedUrl(route);
    if (!url) return false;
    const firstSegment = url.split('/')[0];
    if (firstSegment === 'app') {
      return true;
    }
    const app = this.registry.getById(firstSegment);
    return !!(app && app.externalUrl);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const url = this.getResolvedUrl(route);
    if (!url) return;
    if (handle) {
      this.handlers.set(url, handle);
    } else {
      this.handlers.delete(url);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!route.routeConfig || !route.routeConfig.component) {
      return false;
    }
    const url = this.getResolvedUrl(route);
    return !!url && this.handlers.has(url);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!route.routeConfig || !route.routeConfig.component) {
      return null;
    }
    const url = this.getResolvedUrl(route);
    if (!url) return null;
    return this.handlers.get(url) ?? null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  private getResolvedUrl(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .map((v) => v.url.map((segment) => segment.path).join('/'))
      .filter(Boolean)
      .join('/');
  }
}
